import { isDrawableByPdfMake } from "../pdf-image.js";
import type { Content, TableCell, TDocumentDefinitions } from "pdfmake/interfaces.js";
import type { NdtDocumentCommon } from "../../../domain/ndt/ndt-report-common.js";
import type { DocumentColors, DocumentVisual } from "../../../domain/studio/visual-design.js";
import { HEADER_PAGE_MARGIN_TOP, INSPECTION_REPORT_HEADER_STYLES, buildInspectionReportHeader } from "./inspection-report-header.js";
import { fitBox, isPdfDrawableImage } from "./image-size.js";

/**
 * Peças documentais comuns aos relatórios de END por método (PM, USC):
 * cabeçalho compartilhado INSPECT IA, faixas de seção, grade rótulo/valor,
 * croqui, evidências, termo e signatários — o mesmo visual do LP/ME. O LP
 * mantém o seu builder; este kit só evita duplicar o que é idêntico.
 */

export const USABLE_WIDTH = 515;
const CROQUI_MAX: [number, number] = [497, 260];
const PHOTO_MAX: [number, number] = [400, 240];
const SHORT_COMMENT_CHARS = 400;

export const KIT_COLORS = {
  blue: "#1B4F8F",
  bandFill: "#DCE8F7",
  headFill: "#EAF1FA",
  border: "#C9D3E0",
  label: "#5A6572",
  text: "#1F2933",
  muted: "#6B7683"
};

export const LAUDO_FILL: Record<string, { fill: string; color: string }> = {
  APROVADO: { fill: "#D9F2DF", color: "#1E6B34" },
  REPROVADO: { fill: "#F9D9D9", color: "#9B1C1C" },
  EXAME_COMPLEMENTAR: { fill: "#FBEFD0", color: "#8A5A00" },
  EM_ELABORACAO: { fill: "#E9ECEF", color: "#4B5563" }
};

export const GRID_LAYOUT = {
  hLineWidth: () => 0.5,
  vLineWidth: () => 0.5,
  hLineColor: () => KIT_COLORS.border,
  vLineColor: () => KIT_COLORS.border,
  paddingTop: () => 3,
  paddingBottom: () => 3,
  paddingLeft: () => 5,
  paddingRight: () => 5
};

export const IMAGE_BOX_LAYOUT = {
  hLineWidth: () => 0.6,
  vLineWidth: () => 0.6,
  hLineColor: () => KIT_COLORS.border,
  vLineColor: () => KIT_COLORS.border,
  paddingTop: () => 4,
  paddingBottom: () => 4,
  paddingLeft: () => 4,
  paddingRight: () => 4
};

export const BOX_LAYOUT = {
  hLineWidth: () => 0.6,
  vLineWidth: () => 0.6,
  hLineColor: () => KIT_COLORS.border,
  vLineColor: () => KIT_COLORS.border,
  paddingTop: () => 6,
  paddingBottom: () => 6,
  paddingLeft: () => 8,
  paddingRight: () => 8
};

/**
 * STUDIO INSPECT IA — visual do documento em montagem. Vale só DURANTE o
 * `build()` de PM/US (`withDocumentVisual`) e é restaurado no `finally`:
 * nenhum documento herda o visual de outro. As cores são lidas no build e
 * capturadas em constantes — as funções de layout do pdfmake rodam depois.
 * Visual ausente ou padrão = cores e textos de sempre.
 */
let activeVisual: DocumentVisual | null = null;

export function withDocumentVisual<T>(visual: DocumentVisual | null, build: () => T): T {
  const previous = activeVisual;
  activeVisual = visual;
  try {
    return build();
  } finally {
    activeVisual = previous;
  }
}

const kitColor = (key: keyof DocumentColors, fallback: string): string => activeVisual?.colors[key] ?? fallback;

export function value(input: unknown): string {
  if (input === null || input === undefined) return "—";
  const text = String(input).trim();
  return text.length > 0 ? text : "—";
}

export function th(text: string, alignment: "left" | "center" = "left"): TableCell {
  const color = activeVisual?.colors.tableHeaderText ?? null;
  return { text, style: "th", fillColor: kitColor("tableHeaderFill", KIT_COLORS.headFill), alignment, ...(color ? { color } : {}) };
}

export function sectionBand(number: string, title: string): Content {
  const fill = kitColor("sectionTitleFill", KIT_COLORS.bandFill);
  const bar = kitColor("sectionNumberFill", KIT_COLORS.blue);
  const textColor = activeVisual?.colors.sectionTitleText ?? null;
  return {
    table: {
      widths: ["*"],
      body: [[{ text: `${number}. ${title}`, style: "band", fillColor: fill, margin: [4, 2, 0, 2], border: [true, false, false, false], ...(textColor ? { color: textColor } : {}) }]]
    },
    layout: {
      hLineWidth: () => 0,
      vLineWidth: (i: number) => (i === 0 ? 2 : 0),
      vLineColor: () => bar,
      paddingTop: () => 2,
      paddingBottom: () => 2,
      paddingLeft: () => 4,
      paddingRight: () => 4
    },
    margin: [0, 10, 0, 4],
    headlineLevel: 1
  } as Content;
}

/** Um par por coluna e linha; linha com só o par da esquerda ocupa o resto — nenhuma célula vazia. */
export function pairGrid(left: Array<[string, string]>, right: Array<[string, string]>): Content {
  const rows = Math.max(left.length, right.length);
  const body: TableCell[][] = [];
  for (let i = 0; i < rows; i++) {
    const l = left[i];
    const r = right[i];
    if (l && !r) {
      body.push([{ text: l[0], style: "label" }, { text: value(l[1]), style: "value", colSpan: 3 }, {}, {}]);
      continue;
    }
    body.push([
      { text: l ? l[0] : "", style: "label" },
      { text: l ? value(l[1]) : "", style: "value" },
      { text: r ? r[0] : "", style: "label" },
      { text: r ? value(r[1]) : "", style: "value" }
    ]);
  }
  return { table: { widths: [96, "*", 104, "*"], body }, layout: GRID_LAYOUT };
}

/** "01 IDENTIFICAÇÃO" do FORM-PM / FORM-USC. */
export function buildIdentification(model: NdtDocumentCommon): Content {
  const id = model.identification || ({} as NdtDocumentCommon["identification"]);
  return pairGrid(
    [
      ["Cliente:", id.client],
      ["Projeto / Contrato:", id.projectContract],
      ["Unidade / Área:", id.unitArea],
      ["Data do Ensaio:", id.testDate]
    ],
    [
      ["Equipamento / TAG:", id.equipmentTag],
      ["Tipo de Equipamento:", id.equipmentType],
      ["OS / Sub-OS:", id.osSubOs],
      ["Inspetor Responsável:", id.inspector]
    ]
  );
}

export function buildHeader(model: NdtDocumentCommon, title: string, documentType: string): Content {
  const fields = [
    { label: "Rel. cliente nº", value: model.clientReportNumber || "-" },
    { label: "Rel. INSPECT IA nº", value: model.reportNumber || "-" },
    { label: "Revisão", value: model.revision || "00" },
    { label: "Data de emissão", value: model.issueDate || "-" },
    { label: "Ordem de Serviço", value: model.osNumber || "-" }
  ];
  if (model.subOsNumber) fields.push({ label: "Sub-OS", value: model.subOsNumber });
  return buildInspectionReportHeader({
    title,
    subtitle: "Sistema de Inspeção e Integridade Mecânica",
    qrReference: model.qrPayload || `INSPECT-IA|${documentType}|${model.reportNumber || ""}`,
    qrSize: 72,
    fieldsWidth: 186,
    fields,
    unitArea: model.identification?.unitArea || "-",
    tag: model.identification?.equipmentTag || "-",
    visual: model.visual ?? null
  });
}

export function buildFooter(model: NdtDocumentCommon, formCode: string, documentTitle: string) {
  const visual = model.visual ?? null;
  const border = visual?.colors.border ?? KIT_COLORS.border;
  const identification = visual && !visual.footerShowReportType
    ? `INSPECT IA | REV.${model.revision || "00"}`
    : `INSPECT IA | ${documentTitle} | ${formCode} | REV.${model.revision || "00"}`;
  const institutional = visual?.footerInstitutionalText?.trim() || "";
  return (currentPage: number, pageCount: number): Content => ({
    margin: [40, 6, 40, 0],
    stack: [
      { canvas: [{ type: "line", x1: 0, y1: 0, x2: USABLE_WIDTH, y2: 0, lineWidth: 0.5, lineColor: border }] },
      {
        margin: [0, 5, 0, 0],
        columns: [
          { text: identification, style: "footerText" },
          { text: `Página ${currentPage} de ${pageCount}`, style: "footerText", alignment: "right" }
        ]
      },
      ...(institutional ? [{ text: institutional, style: "footerText", margin: [0, 2, 0, 0] } as Content] : [])
    ]
  });
}

/** Título de seção nunca fica sozinho no fim da página (pdfmake 0.3: nodeInfo + consultas). */
export function pageBreakBefore(currentNode: any, nodeQueries: any): boolean {
  return currentNode.headlineLevel === 1 &&
    nodeQueries.getFollowingNodesOnPage().filter((n: any) => n.style !== "band").length === 0;
}

export function buildCroquiBox(croqui: NdtDocumentCommon["croqui"]): Content {
  if (!croqui || !isPdfDrawableImage(croqui.image)) {
    return { table: { widths: ["*"], body: [[{ text: "Nenhum croqui anexado.", style: "empty", alignment: "center" }]] }, layout: BOX_LAYOUT };
  }
  return {
    table: { widths: ["*"], body: [[{ image: croqui.image, fit: fitBox(croqui.image, Math.min(USABLE_WIDTH - 18, CROQUI_MAX[0]), CROQUI_MAX[1]), alignment: "center" }]] },
    layout: BOX_LAYOUT
  };
}

/** Evidências uma abaixo da outra: FOTO XX + imagem + componente/localização (+ comentário curto) indivisíveis. */
export function buildPhotoBlocks(photos: NdtDocumentCommon["photos"]): Content {
  const list = Array.isArray(photos) ? photos.filter(p => isPdfDrawableImage(p.image)) : [];
  if (list.length === 0) {
    return { table: { widths: ["*"], body: [[{ text: "Nenhuma evidência fotográfica anexada.", style: "empty", alignment: "center" }]] }, layout: BOX_LAYOUT };
  }
  const captionRow = (label: string, v: string): TableCell[] => [{ text: label, style: "label" }, { text: value(v), style: "value" }];
  return {
    stack: list.map((photo, index) => {
      const comment = { table: { widths: [88, "*"], body: [captionRow("Comentário:", photo.comment)] }, layout: GRID_LAYOUT };
      const shortComment = (photo.comment || "").length <= SHORT_COMMENT_CHARS;
      const block = {
        unbreakable: true,
        stack: [
          { text: photo.number.toUpperCase(), style: "photoNumber", margin: [0, 0, 0, 3] },
          { table: { widths: ["*"], body: [[{ image: photo.image, fit: fitBox(photo.image, Math.min(USABLE_WIDTH - 20, PHOTO_MAX[0]), PHOTO_MAX[1]), alignment: "center" }]] }, layout: IMAGE_BOX_LAYOUT },
          { table: { widths: [88, "*"], body: [captionRow("Componente:", photo.item), captionRow("Localização:", photo.location)] }, layout: GRID_LAYOUT },
          ...(shortComment ? [comment] : [])
        ]
      };
      return { margin: [0, index === 0 ? 0 : 10, 0, 0], stack: (shortComment ? [block] : [block, comment]) as Content[] };
    })
  };
}

export function buildTextBox(text: string | null | undefined): Content {
  const v = typeof text === "string" ? text.trim() : "";
  return { table: { widths: ["*"], body: [[v ? { text: v, style: "boxText" } : { text: "—", style: "empty" }]] }, layout: BOX_LAYOUT };
}

/** "LEGENDA E CRITÉRIO DE RESULTADO" do modelo. */
export function buildLegend(model: NdtDocumentCommon): Content {
  return {
    table: {
      widths: ["*"],
      body: [[{
        stack: [
          { text: model.legend, style: "subTitle", margin: [0, 0, 0, 4] },
          ...(model.legendNotes || []).map(n => ({ text: n, style: "boxText" }))
        ]
      }]]
    },
    layout: BOX_LAYOUT
  };
}

function signatureImage(image: string | null | undefined): Content {
  if (isDrawableByPdfMake(image)) {
    return { image, fit: [120, 38], alignment: "center", margin: [0, 6, 0, 4] };
  }
  return {
    stack: [
      { text: " ", margin: [0, 14, 0, 0] },
      { canvas: [{ type: "line", x1: 20, y1: 0, x2: 130, y2: 0, lineWidth: 0.5, lineColor: KIT_COLORS.muted }] },
      { text: " ", margin: [0, 2, 0, 0] }
    ]
  };
}

/** "TERMO DE RESPONSABILIDADE & SIGNATÁRIOS": termo + INSPETOR DE END / ENGENHEIRO-PLH / FISCAL DO CONTRATO. */
export function buildSignatories(model: NdtDocumentCommon): Content {
  const list = Array.isArray(model.signatories) ? model.signatories : [];
  const roles = ["INSPETOR", "PLH", "FISCAL"] as const;
  const labels: Record<string, string> = { INSPETOR: "INSPETOR DE END", PLH: "ENGENHEIRO / PLH", FISCAL: "FISCAL DO CONTRATO" };
  // Bloco visual de assinatura (Studio): o que aparece; o NOME nunca é ocultado.
  const show = model.visual?.signature ?? { showImage: true, showName: true, showRegistration: true, showFunction: true, showDate: true };
  const border = model.visual?.colors.border ?? KIT_COLORS.border;
  const cells: TableCell[] = roles.map(role => {
    const s = list.find(x => x.role === role);
    const stack: Content[] = [{ text: labels[role]!, style: "sigRole", alignment: "center" }];
    if (show.showImage) stack.push(signatureImage(s?.signatureImage));
    stack.push({ text: value(s?.name), style: "sigName", alignment: "center" });
    if (show.showFunction) stack.push({ text: value(s?.functionTitle), style: "sigMeta", alignment: "center" });
    if (show.showRegistration) stack.push({ text: s?.credential ? s.credential : "—", style: "sigMeta", alignment: "center" });
    if (show.showDate) stack.push({ text: `Data: ${value(s?.date)}`, style: "sigMeta", alignment: "center", margin: [0, 2, 0, 0] });
    return { stack, margin: [0, 4, 0, 4] };
  });
  return {
    stack: [
      buildTextBox(model.responsibilityTerm),
      {
        margin: [0, 6, 0, 0],
        table: { widths: ["*", "*", "*"], body: [cells], dontBreakRows: true },
        layout: { hLineWidth: () => 0.6, vLineWidth: () => 0.6, hLineColor: () => border, vLineColor: () => border, paddingTop: () => 4, paddingBottom: () => 4, paddingLeft: () => 6, paddingRight: () => 6 }
      }
    ]
  };
}

export function kitStyles(): TDocumentDefinitions["styles"] {
  return {
    ...INSPECTION_REPORT_HEADER_STYLES,
    headerLogo: { ...INSPECTION_REPORT_HEADER_STYLES.headerLogo, fontSize: 15 },
    headerSub: { ...INSPECTION_REPORT_HEADER_STYLES.headerSub, fontSize: 8 },
    headerDocTitle: { ...INSPECTION_REPORT_HEADER_STYLES.headerDocTitle, fontSize: 17, lineHeight: 1.1, margin: [0, 2, 0, 0] },
    headerField: { ...INSPECTION_REPORT_HEADER_STYLES.headerField, fontSize: 9 },
    footerText: { fontSize: 6.8, color: KIT_COLORS.muted },
    band: { fontSize: 8.5, bold: true, color: KIT_COLORS.blue },
    subTitle: { fontSize: 7.5, bold: true, color: KIT_COLORS.blue },
    label: { fontSize: 7, color: KIT_COLORS.label },
    value: { fontSize: 7.2, color: KIT_COLORS.text },
    th: { fontSize: 6.8, bold: true, color: KIT_COLORS.blue },
    td: { fontSize: 7, color: KIT_COLORS.text },
    photoNumber: { fontSize: 6.8, bold: true, color: KIT_COLORS.text },
    sigRole: { fontSize: 6.5, bold: true, color: KIT_COLORS.text },
    sigName: { fontSize: 7.5, bold: true, color: KIT_COLORS.text },
    sigMeta: { fontSize: 6.8, color: KIT_COLORS.muted },
    boxText: { fontSize: 7.2, color: KIT_COLORS.text, lineHeight: 1.25 },
    empty: { fontSize: 7, color: KIT_COLORS.muted, italics: true },
    jointTitle: { fontSize: 7.5, bold: true, color: KIT_COLORS.text }
  };
}

export const KIT_PAGE_MARGINS: [number, number, number, number] = [40, HEADER_PAGE_MARGIN_TOP + 12, 40, 44];
